'use client'

import { ReactNode } from 'react'
import { usePathname } from 'next/navigation'
import type { PublicTenant } from '@/types/seo'
import { AuthProvider } from '@/contexts/AuthContext'
import { TenantProvider } from '@/contexts/TenantContext'
import { ServicesProvider } from '@/contexts/ServicesContext'
import { BarbersProvider } from '@/contexts/BarbersContext'
import { BranchesProvider } from '@/contexts/BranchesContext'
import { ToastProvider } from '@/components/ToastContainer'
import ErrorBoundary from '@/components/ErrorBoundary'

export function Providers({ children, initialTenant }: { children: ReactNode; initialTenant: PublicTenant | null }) {
  const pathname = usePathname()
  const isAdmin = pathname.startsWith('/admin') || pathname.startsWith('/profesional')

  // The platform landing is tenant-independent and does not consume these contexts.
  // Skip their initialization there so a visit to /landing never requests tenant APIs.
  if (pathname === '/landing') {
    return <ErrorBoundary>{children}</ErrorBoundary>
  }

  const tenantForContext = initialTenant
    ? {
        ...initialTenant,
        logo_url: initialTenant.logo_url || undefined,
        address: initialTenant.address || undefined,
        phone: initialTenant.phone || undefined,
        whatsapp: initialTenant.whatsapp || undefined,
        instagram: initialTenant.instagram || undefined,
        facebook: initialTenant.facebook || undefined,
        maps_url: initialTenant.maps_url || undefined,
        welcome_message: initialTenant.welcome_message || undefined,
      }
    : null

  return (
    <ErrorBoundary>
      <AuthProvider>
        <TenantProvider skipFetch={isAdmin} initialTenant={tenantForContext}>
          <BranchesProvider skipFetch={isAdmin}>
            <ServicesProvider skipFetch={isAdmin}>
              <BarbersProvider skipFetch={isAdmin}>
                <ToastProvider>
                  {children}
                </ToastProvider>
              </BarbersProvider>
            </ServicesProvider>
          </BranchesProvider>
        </TenantProvider>
      </AuthProvider>
    </ErrorBoundary>
  )
}
